import { cn } from '@/lib/utils'
import { useEffect, useEffectEvent, useState } from 'react'
import type { NumericFormatProps } from 'react-number-format'
import { NumericFormat } from 'react-number-format'
import { FAIcon } from './fa-icon'
import { Button, InputWithChangeEvent } from './ui'

export interface NumberInputProps
  extends Omit<NumericFormatProps, 'value' | 'onValueChange' | 'onChange'> {
  ref?: React.Ref<HTMLInputElement>

  value?: number
  defaultValue?: number

  placeholder?: string
  stepper?: number
  thousandSeparator?: string
  min?: number
  max?: number
  suffix?: string
  prefix?: string
  fixedDecimalScale?: boolean
  decimalScale?: number

  onChange?: (value: number | undefined) => void
}

export function NumberInput({
  ref,
  className,

  value: controlledValue,
  defaultValue,

  placeholder,
  stepper = 1,
  thousandSeparator = ' ',
  min = -Infinity,
  max = Infinity,
  fixedDecimalScale = false,
  decimalScale = 0,
  suffix,
  prefix,

  onChange,

  ...props
}: NumberInputProps) {
  const [value, setValue] = useState(controlledValue ?? defaultValue)

  function handleIncrement() {
    setValue(prev =>
      typeof prev === 'undefined' ? stepper : Math.min(prev + stepper, max)
    )
  }

  function handleDecrement() {
    setValue(prev =>
      typeof prev === 'undefined' ? -stepper : Math.max(prev - stepper, min)
    )
  }

  const setValueEffect = useEffectEvent((newValue: number | undefined) => {
    setValue(newValue)
  })

  useEffect(() => {
    if (typeof controlledValue !== 'undefined') setValueEffect(controlledValue)
  }, [controlledValue])

  function handleKeyDown(evt: React.KeyboardEvent) {
    if (evt.key === 'ArrowUp') {
      evt.preventDefault()
      handleIncrement()
    } else if (evt.key === 'ArrowDown') {
      evt.preventDefault()
      handleDecrement()
    }
  }

  function handleBlur() {
    if (typeof value === 'undefined') return

    if (value < min) {
      setValue(min)
      if (!!ref && 'current' in ref && !!ref.current)
        ref.current.value = String(min)
    } else if (value > max) {
      setValue(max)
      if (!!ref && 'current' in ref && !!ref.current)
        ref.current.value = String(max)
    }
  }
  function handleChange(values: {
    value: string
    floatValue: number | undefined
  }) {
    const newValue =
      values.floatValue === undefined ? undefined : values.floatValue
    setValue(newValue)
    onChange?.(newValue)
  }

  return (
    <div className={cn('grid grid-flow-col grid-cols-[1fr_auto]', className)}>
      <NumericFormat
        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none rounded-r-none relative"
        customInput={InputWithChangeEvent}
        value={value}
        thousandSeparator={thousandSeparator}
        decimalScale={decimalScale}
        fixedDecimalScale={fixedDecimalScale}
        allowNegative={min < 0}
        valueIsNumericString
        max={max}
        min={min}
        suffix={suffix}
        prefix={prefix}
        placeholder={placeholder}
        getInputRef={ref}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onValueChange={handleChange}
        {...props}
      />

      <div className="flex flex-col">
        <Button
          type="button"
          className="px-2 h-1/2 rounded-l-none rounded-br-none border-input border-l-0 border-b-[0.5px] focus-visible:relative hover:border-stone-300"
          aria-label="Increase value"
          variant="outline"
          disabled={value === max}
          onClick={handleIncrement}
        >
          <FAIcon className="text-stone-500" icon="chevron-up" />
        </Button>

        <Button
          type="button"
          className="px-2 h-1/2 rounded-l-none rounded-tr-none border-input border-l-0 border-t-[0.5px] focus-visible:relative hover:border-stone-300"
          aria-label="Decrease value"
          variant="outline"
          disabled={value === min}
          onClick={handleDecrement}
        >
          <FAIcon className="text-stone-500" icon="chevron-down" />
        </Button>
      </div>
    </div>
  )
}
